// eslint-disable-next-line no-restricted-imports
import { css } from "@emotion/react";
// eslint-disable-next-line no-restricted-imports
import styled from "@emotion/styled";
import { t } from "ttag";
import _ from "underscore";

import { Panel } from "metabase/admin/performance/components/StrategyEditorForDatabases.styled";
import { rootId } from "metabase/admin/performance/constants/simple";
import type { UpdateTargetId } from "metabase/admin/performance/types";
import { getShortStrategyLabel } from "metabase/admin/performance/utils";
import { FixedSizeIcon, Flex, Stack, Text } from "metabase/ui";
import type { CacheStrategy, CacheableModel } from "metabase-types/api";

export const StrategyFormLauncherPanel = ({
  configs,
  targetId,
  updateTargetId,
  databases,
  isStrategyFormDirty,
}: {
  configs: {
    model: CacheableModel;
    model_id: number;
    strategy: CacheStrategy;
  }[];
  targetId: number | null;
  updateTargetId: UpdateTargetId;
  databases: { id: number; name: string }[];
  isStrategyFormDirty: boolean;
  shouldShowResetButton: boolean;
}) => {
  const rootConfig = _.findWhere(configs, { model: "root" });

  const renderLauncher = ({
    id,
    name,
    icon,
    strategy,
    sectionLabel,
  }: {
    id: number;
    name: string;
    icon: "star" | "database";
    strategy?: CacheStrategy;
    sectionLabel?: string;
  }) => {
    const isSelected = targetId === id;
    const displayStrategy = getShortStrategyLabel(strategy) ?? t`Use default`;

    return (
      <Stack key={id} gap="xs">
        {sectionLabel && (
          <Text
            size="sm"
            fw={800}
            c="text-secondary"
            tt="uppercase"
            ls="0.08em"
          >
            {sectionLabel}
          </Text>
        )}
        <LauncherCard
          isSelected={isSelected}
          isRoot={id === rootId}
          onClick={() => updateTargetId(id, isStrategyFormDirty)}
          aria-label={
            id === rootId
              ? t`Edit default policy (currently: ${displayStrategy})`
              : t`Edit policy for database '${name}' (currently: ${displayStrategy})`
          }
        >
          <Flex align="center" justify="space-between" w="100%" gap="sm">
            <Flex align="center" gap="sm" style={{ minWidth: 0, flex: 1 }}>
              <FixedSizeIcon name={icon} />
              <Text fw={700} size="lg" truncate>
                {name}
              </Text>
            </Flex>
            <StrategyBadge isSelected={isSelected}>
              {displayStrategy}
            </StrategyBadge>
          </Flex>
        </LauncherCard>
      </Stack>
    );
  };

  return (
    <Panel
      role="group"
      style={{
        backgroundColor: "var(--mb-color-background-secondary)",
        padding: "1.25rem",
      }}
    >
      <Stack gap="xl" h="100%">
        {renderLauncher({
          id: rootId,
          name: t`Default policy`,
          icon: "star",
          strategy:
            rootConfig?.strategy ?? ({ type: "nocache" } as CacheStrategy),
          sectionLabel: t`Default`,
        })}
        <Stack gap="sm" style={{ overflowY: "auto" }}>
          <Text
            size="sm"
            fw={800}
            c="text-secondary"
            tt="uppercase"
            ls="0.08em"
          >
            {t`Databases`}
          </Text>
          {databases.map((db) =>
            renderLauncher({
              id: db.id,
              name: db.name,
              icon: "database",
              strategy:
                _.findWhere(configs, { model_id: db.id })?.strategy ??
                rootConfig?.strategy,
            }),
          )}
        </Stack>
      </Stack>
    </Panel>
  );
};

const LauncherCard = styled.button<{ isSelected?: boolean; isRoot?: boolean }>`
  width: 100%;
  border-radius: 0.9rem;
  border: 1px solid var(--mb-color-border);
  background: ${({ isRoot }) =>
    isRoot
      ? "var(--mb-color-background-tertiary)"
      : "var(--mb-color-background-primary)"};
  padding: 1rem 1.1rem;
  color: inherit;
  text-align: left;
  transition:
    border-color 140ms ease,
    background-color 140ms ease,
    box-shadow 140ms ease,
    transform 140ms ease;

  ${({ isSelected }) =>
    isSelected &&
    css`
      border-color: var(--mb-color-brand);
      box-shadow: 0 0 0 1px var(--mb-color-brand);
      background: color-mix(
        in srgb,
        var(--mb-color-brand) 10%,
        var(--mb-color-background-primary)
      );
    `}

  &:hover {
    border-color: var(--mb-color-brand-light);
    background: color-mix(
      in srgb,
      var(--mb-color-brand) 6%,
      var(--mb-color-background-primary)
    );
  }

  &:focus-visible {
    outline: 2px solid var(--mb-color-focus-ring);
    outline-offset: 2px;
  }
`;

const StrategyBadge = styled.div<{ isSelected?: boolean }>`
  flex: 0 0 auto;
  border-radius: 999px;
  border: 1px solid
    ${({ isSelected }) =>
      isSelected ? "var(--mb-color-brand)" : "var(--mb-color-border)"};
  padding: 0.35rem 0.7rem;
  font-size: 0.9rem;
  font-weight: 700;
  line-height: 1.2;
  color: ${({ isSelected }) =>
    isSelected
      ? "var(--mb-color-brand-light)"
      : "var(--mb-color-text-secondary)"};
  background: ${({ isSelected }) =>
    isSelected
      ? "color-mix(in srgb, var(--mb-color-brand) 12%, transparent)"
      : "var(--mb-color-background-primary)"};
  white-space: nowrap;
`;
